import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../services/apiService';
import { useNotifications } from '../hooks/useNotifications';
import { useAuth } from '../contexts/AuthContext';
import { invalidateBalanceQueries } from '../services/moneyService';

interface ClaimableBonus {
  type: 'first_login' | 'daily';
  coupon: {
    id: string;
    name: string;
    amount: number;
    description: string;
  };
}

const BonusClaimModal: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { success, warning } = useNotifications();
  const { refreshUserData } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [currentBonus, setCurrentBonus] = useState<ClaimableBonus | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isReady, setIsReady] = useState(false);

  // Wait a bit for auth to be ready, and check if modal was dismissed this session
  useEffect(() => {
    const wasDismissed = sessionStorage.getItem('bonusModalDismissed') === 'true';
    if (wasDismissed) {
      console.log('[BonusModal] Modal was dismissed this session');
      setDismissed(true);
    }
    
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Check for claimable bonuses
  const { data: claimableData, refetch, isError, error } = useQuery({
    queryKey: ['claimable-bonuses'],
    queryFn: async () => {
      try {
        // api.post already returns response.data
        const data: any = await api.post('/api/Events/checkClaimableBonuses');
        
        // data is { status: 0, claimable: [...], message: {...} }
        if (data && data.claimable) {
          return data.claimable as ClaimableBonus[];
        }
        
        return [] as ClaimableBonus[];
      } catch (err) {
        console.error('[BonusModal] Error fetching claimable bonuses:', err);
        return [] as ClaimableBonus[];
      }
    },
    enabled: isReady,
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
    staleTime: 30000,
    retry: 2,
  });

  // Log errors
  useEffect(() => {
    if (isError) {
      console.error('[BonusModal] Query error:', error);
    }
  }, [isError, error]);

  // Claim bonus mutation
  const claimMutation = useMutation({
    mutationFn: async (couponId: string) => {
      // api.post already returns response.data
      const data: any = await api.post('/api/Events/claimCoupon', { couponId });
      // Check for error status
      if (data.status === 1 || data.message?.warning) {
        throw new Error(data.message?.warning || t('bonusModal.failedToClaim'));
      }
      return data;
    },
    onSuccess: async (data: any) => {
      // Refresh user data from AuthContext (updates localStorage)
      try {
        await refreshUserData();
      } catch (e) {
        console.error('[BonusModal] Error refreshing user data:', e);
      }
      
      // Invalidate React Query caches (balance-related + claimable)
      invalidateBalanceQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ['userInfo'] });
      
      // Trigger balance update event for header
      window.dispatchEvent(new Event('userBalanceUpdated'));
      
      // Clear the dismiss flag since bonus is claimed
      sessionStorage.removeItem('bonusModalDismissed');
      
      setIsAnimating(true);
      setTimeout(() => {
        success(t('bonusModal.successTitle'), t('bonusModal.successReceived', { amount: data.amount }));
        setIsVisible(false);
        setCurrentBonus(null);
        setIsAnimating(false);
        setDismissed(true);
        refetch();
      }, 1500);
    },
    onError: (error: any) => {
      const message = error.message || t('bonusModal.failedToClaim');
      warning(t('bonusModal.cannotClaim'), message);
      setIsVisible(false);
      setCurrentBonus(null);
      setDismissed(true);
      refetch();
    },
  });

  // Show modal when there are claimable bonuses
  useEffect(() => {
    if (dismissed) return;
    
    if (claimableData && claimableData.length > 0) {
      // Prioritize first_login over daily
      const firstLogin = claimableData.find(b => b.type === 'first_login');
      const daily = claimableData.find(b => b.type === 'daily');
      
      const bonusToShow = firstLogin || daily;
      console.log('[BonusModal] Bonus to show:', bonusToShow);
      
      if (bonusToShow && !currentBonus) {
        console.log('[BonusModal] Showing modal for:', bonusToShow.type);
        setCurrentBonus(bonusToShow);
        setIsVisible(true);
      }
    }
  }, [claimableData, currentBonus, dismissed]);

  if (!isVisible || !currentBonus) return null;

  const isFirstLogin = currentBonus.type === 'first_login';

  return (
    <div className="fixed inset-0 z-[1000] flex items-end justify-center sm:items-center p-3 sm:p-4 md:p-6 overflow-y-auto overscroll-contain">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        aria-hidden
      />
      
      {/* Confetti effect for first login */}
      {isFirstLogin && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'][Math.floor(Math.random() * 6)],
                width: '10px',
                height: '10px',
                borderRadius: Math.random() > 0.5 ? '50%' : '0',
              }}
            />
          ))}
        </div>
      )}

      {/* Modal: full width on xs, max-width on larger; scroll if content + keyboard */}
      <div
        className={`relative w-full max-w-[calc(100vw-1.5rem)] sm:max-w-md mx-auto my-auto sm:my-0 max-h-[min(90dvh,90vh)] flex flex-col transform transition-all duration-500 ${
          isAnimating ? 'scale-110 opacity-0' : 'scale-100 opacity-100'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bonus-modal-title"
      >
        <div
          className={`relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl flex flex-col min-h-0 max-h-full ${
            isFirstLogin
              ? 'bg-gradient-to-br from-yellow-500 via-orange-500 to-red-500'
              : 'bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500'
          }`}
        >
          {/* Glow effect */}
          <div className={`absolute inset-0 ${
            isFirstLogin 
              ? 'bg-gradient-to-r from-yellow-400/20 to-transparent' 
              : 'bg-gradient-to-r from-blue-400/20 to-transparent'
          } animate-pulse`} />

          <div className="relative p-4 pt-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:p-6 md:p-8 text-center overflow-y-auto overscroll-contain flex flex-col min-h-0">
            {/* Icon */}
            <div className="mb-3 sm:mb-5 md:mb-6 shrink-0">
              <div className="inline-block animate-bounce">
                <span className="text-5xl sm:text-7xl md:text-8xl leading-none" aria-hidden>
                  {isFirstLogin ? '🎁' : '🌟'}
                </span>
              </div>
            </div>

            {/* Title */}
            <h2
              id="bonus-modal-title"
              className="text-xl sm:text-2xl md:text-3xl font-black text-white mb-2 drop-shadow-lg break-words px-1"
            >
              {isFirstLogin ? t('bonusModal.welcomeTitle') : t('bonusModal.dailyTitle')}
            </h2>

            {/* Amount */}
            <div className="bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 mb-4 sm:mb-6 shrink-0">
              <div className="text-sm sm:text-base md:text-lg text-white/80 mb-1">
                {t('bonusModal.youWillReceive')}
              </div>
              <div className="text-3xl sm:text-4xl md:text-5xl font-black text-white drop-shadow-lg tabular-nums break-all">
                ${currentBonus.coupon.amount.toLocaleString()}
              </div>
              <div className="text-white/80 mt-1 text-sm sm:text-base">{t('bonusModal.freeCredits')}</div>
            </div>

            {/* Claim Button — min 44px height for touch targets */}
            <button
              type="button"
              onClick={() => claimMutation.mutate(currentBonus.coupon.id)}
              disabled={claimMutation.isPending || isAnimating}
              className={`w-full min-h-[48px] py-3 sm:py-4 px-4 sm:px-8 rounded-xl sm:rounded-2xl font-bold text-base sm:text-lg md:text-xl transition-all transform hover:scale-[1.02] sm:hover:scale-105 active:scale-[0.98] disabled:opacity-50 disabled:transform-none touch-manipulation ${
                isFirstLogin
                  ? 'bg-white text-orange-600 hover:bg-yellow-50 shadow-lg shadow-orange-500/50'
                  : 'bg-white text-indigo-600 hover:bg-blue-50 shadow-lg shadow-indigo-500/50'
              }`}
            >
              {claimMutation.isPending || isAnimating ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('bonusModal.claiming')}
                </span>
              ) : (
                t('bonusModal.claimAmount', { amount: currentBonus.coupon.amount })
              )}
            </button>

            {/* Skip button - for both first login and daily */}
            <button
              type="button"
              onClick={() => {
                setIsVisible(false);
                setCurrentBonus(null);
                setDismissed(true);
                sessionStorage.setItem('bonusModalDismissed', 'true');
              }}
              className="mt-3 sm:mt-4 py-2 min-h-[44px] inline-flex items-center justify-center text-white/70 hover:text-white text-sm sm:text-base underline touch-manipulation"
            >
              {isFirstLogin ? t('bonusModal.claimLaterCoupon') : t('bonusModal.maybeLater')}
            </button>

            {/* First login reminder */}
            {isFirstLogin && (
              <p className="mt-2 sm:mt-3 text-white/70 text-xs sm:text-sm leading-relaxed px-1">
                {t('bonusModal.claimAnytimeCoupon')}
              </p>
            )}
            {/* Daily bonus: explain streak so users know consecutive logins increase the amount */}
            {!isFirstLogin && (
              <p className="mt-2 sm:mt-3 text-white/80 text-xs sm:text-sm leading-relaxed px-1">
                {t('bonusModal.dailyStreakHint')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* CSS for confetti animation */}
      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default BonusClaimModal;

